import { CobaltButton } from '@cae/cobalt-react/components/button';
import {
  CobaltTable,
  CobaltTableBody,
  CobaltTableCell,
  CobaltTableHead,
  CobaltTableRow,
} from '@cae/cobalt-react/components/table';
import { CobaltTag } from '@cae/cobalt-react/components/tag';
import { CobaltIconDownloadOutline } from '@cae/cobalt-react/icons/outline/CobaltIconDownloadOutline';
import { useDownloadDocument } from '@features/documents/hooks/use-download-document/useDownloadDocument';
import { DataValue } from '@shared/components/data-value/DataValue';
import { formatDateShort } from '@shared/utils/format-date/formatDate';
import * as React from 'react';

import type { SubmittedDocumentsTableProps } from './SubmittedDocumentsTable.types';

import styles from './SubmittedDocumentsTable.module.css';
import {
  getSubmittedStatusLabel,
  getSubmittedStatusTagColor,
} from './SubmittedDocumentsTable.utils';

const SubmittedDocumentsTable = React.memo(
  ({
    documents,
    organizationId,
    canDownload,
  }: Readonly<SubmittedDocumentsTableProps>): React.ReactElement => {
    const { download, isDocumentDownloading } = useDownloadDocument();

    const handleDownload = React.useCallback(
      (documentId: string): void => {
        download(documentId, organizationId);
      },
      [download, organizationId]
    );

    return (
      <CobaltTable size="x-large">
        <CobaltTableHead>
          <CobaltTableRow>
            <CobaltTableCell header className={styles.dataColumn}>
              Name
            </CobaltTableCell>
            <CobaltTableCell header className={styles.dataColumn}>
              Case number
            </CobaltTableCell>
            <CobaltTableCell header className={styles.dataColumn}>
              Submitted on
            </CobaltTableCell>
            <CobaltTableCell header className={styles.dataColumn}>
              Status
            </CobaltTableCell>
            <CobaltTableCell header className={styles.downloadCell} />
          </CobaltTableRow>
        </CobaltTableHead>
        <CobaltTableBody>
          {documents.map(doc => (
            <CobaltTableRow
              key={doc.id}
              data-testid={`submitted-document-row-${doc.id}`}
              interactive
            >
              <CobaltTableCell className={styles.dataColumn}>
                <DataValue value={doc.name} />
              </CobaltTableCell>
              <CobaltTableCell className={styles.dataColumn}>
                <DataValue
                  value={doc.caseNumber}
                  fallback={
                    doc.submittedStatus === 'Submitted'
                      ? '(Pending)'
                      : undefined
                  }
                />
              </CobaltTableCell>
              <CobaltTableCell className={styles.dataColumn}>
                <DataValue value={formatDateShort(doc.submittedAt)} />
              </CobaltTableCell>
              <CobaltTableCell className={styles.dataColumn}>
                {doc.submittedStatus ? (
                  <CobaltTag
                    color={getSubmittedStatusTagColor(doc.submittedStatus)}
                    size="small"
                  >
                    {getSubmittedStatusLabel(doc.submittedStatus)}
                  </CobaltTag>
                ) : (
                  <DataValue value={null} />
                )}
              </CobaltTableCell>
              <CobaltTableCell className={styles.downloadCell}>
                {canDownload && (
                  <CobaltButton
                    variant="ghost"
                    size="small"
                    aria-label={`Download ${doc.name}`}
                    onClick={() => handleDownload(doc.id)}
                    disabled={isDocumentDownloading(doc.id)}
                    pending={isDocumentDownloading(doc.id)}
                  >
                    <CobaltIconDownloadOutline />
                  </CobaltButton>
                )}
              </CobaltTableCell>
            </CobaltTableRow>
          ))}
        </CobaltTableBody>
      </CobaltTable>
    );
  }
);

SubmittedDocumentsTable.displayName = 'SubmittedDocumentsTable';

export { SubmittedDocumentsTable };
